import {isEqual} from 'lodash';
import {useMemo} from 'react';

import {useMemoizedSelector} from '../../../../state/hooks';
import {makePanelsBySectionSelector} from '../../../../state/views/panelBankConfig/selectors';
import {getKey} from '../../../../util/panels';
import {useWorkspaceRefsContext} from '../../../Workspace/WorkspaceRefsContext';
import {useWorkspaceDrawerContext} from '../../WorkspaceDrawerContextProvider';

type PanelMetadata = {
  /** Unique ID of the panel */
  __id__: string;
  /** Whether the panel is an unmodified auto panel */
  isAuto: boolean | undefined;
  /** Whether the panel is a regex line plot */
  isRegex: boolean;
};

type ExistingPanelsByKey = {
  /** Includes all panels across entire workspace */
  global: Map<string, PanelMetadata[]>;
  /** Includes panels in the current quick add scope (either specific section, or global) */
  inScope: Map<string, PanelMetadata[]>;
};

export const useExistingPanelsByKey = (): ExistingPanelsByKey => {
  const {panelBankConfigRef} = useWorkspaceRefsContext();
  const {targetSectionRef} = useWorkspaceDrawerContext();
  const isGlobalQuickAdd = !targetSectionRef;

  const panelsBySection = useMemoizedSelector(
    makePanelsBySectionSelector,
    panelBankConfigRef
  );

  return useMemo(() => {
    const global = new Map<string, PanelMetadata[]>();
    const inScope = isGlobalQuickAdd
      ? global
      : new Map<string, PanelMetadata[]>();
    // need to exclude last section AKA hidden panels
    panelsBySection.slice(0, -1).forEach(({sectionRef, panels}) => {
      const isTargetSection = isEqual(sectionRef, targetSectionRef);
      panels.forEach(panel => {
        const isRegexPanel =
          panel.viewType === 'Run History Line Plot' &&
          panel.config.useMetricRegex === true;

        const key = getKey(panel);
        if (!key) {
          return;
        }

        const panelMetadata = {
          __id__: panel.__id__,
          isAuto: panel.isAuto,
          isRegex: isRegexPanel,
        };

        const globalList = global.get(key);
        if (!globalList) {
          global.set(key, [panelMetadata]);
        } else {
          globalList.push(panelMetadata);
        }

        if (isTargetSection) {
          const sectionList = inScope.get(key);
          if (!sectionList) {
            inScope.set(key, [panelMetadata]);
          } else {
            sectionList.push(panelMetadata);
          }
        }
      });
    });
    return {global, inScope};
  }, [isGlobalQuickAdd, panelsBySection, targetSectionRef]);
};
